import type { GroupDetail, HomeSummary, InstallmentChoice, Invoice, Me, Module, Partner } from "@genbaryoku/contract";
import { useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { ComponentType, ReactNode } from "react";
import { useMe } from "../auth.jsx";
import type { HeadAction } from "../components/HeadActions.jsx";

/**
 * 土台の画面に、部品・製品が中身を差し込む口（../eigyouryoku/docs/09 §4.1 の2：土台は部品も製品も知らない）。
 *   ・メニュー（左の帯）の項目、画面（ルート）、画面の中の差し込み口（Slot）を、製品の置き場（products/<製品>/）が登録する
 *   ・登録は main.tsx が製品の置き場を import したときに1回だけ。土台の画面は Slot の名前しか知らない
 *   ・どれも「その会社で有効なモジュール」（Me.tenant.modules）で出す／出さないを決める。門は API の requireModule
 *   ・差し込み口にするほど大きくない部品（選択肢の1つ・欄の1つ）は hasModule／useModule で出し分ける（#142 段2：field＝労務費・5項目・材工区分・施主・写真）
 */

export type NavEntry = {
  to: string;
  label: string;
  /** 左の帯のアイコン（24×24 の path） */
  icon: ReactNode;
  module: Module;
  /** どのメニューのあとに置くか（今の並びの key） */
  after: "home" | "groups" | "estimates" | "invoices" | "partners";
};
/** lineEntry：明細を入力する画面＝左のメニューを畳んで始める（#169。発注書・納品書など） */
export type RouteEntry = { path: string; module: Module; element: () => ReactNode; lineEntry?: boolean };

/** 差し込み口の名前と、渡すもの */
export type SlotProps = {
  /** 請求書の画面の上（出した請求書）。営業力：入金の記録の帯 */
  "invoice.head": { invoice: Invoice };
  /** 請求書の画面の上の紙の中（出した請求書）。sales：分割払いの「お支払い予定」（#267 3b） */
  "invoice.schedule": { invoice: Invoice };
  /** 請求書の発行前の確認の中。sales：分割払いの選択と回の表（#267 3b）。選んだ値は請求書の画面が持ち、発行の本文に入れる */
  "invoice.issue": { invoice: Invoice; value: InstallmentChoice; onChange: (v: InstallmentChoice) => void };
  /** {customer}の画面の下（幅いっぱい）。sales：「入金予定」（#267 3b） */
  "partner.receivables": { partner: Partner };
  /** ホームのカードの下。営業力：入金待ち */
  "home.cards": { home: HomeSummary };
  /** 案件の画面の「入金」タブの中身 */
  "group.payments": { group: GroupDetail };
  /** 案件の画面の 案件詳細 と 社内メモ のあいだ。受け取った書類の枠（部品 received、門は core。../eigyouryoku/docs/13） */
  "group.received": { group: GroupDetail };
  /** スマホの見積の下の帯の「撮る」（field。#142 段2）。撮った写真は写真の引き出しへ */
  "phone.shoot": { onFiles: (files: File[]) => void };
};
export type SlotName = keyof SlotProps;
/**
 * 見出しの右の操作を部品が足す口（#172、docs/10 §5.12）：土台の画面の「主なボタン1つ＋⋯」（components/HeadActions）に、部品の操作を混ぜる。
 *   部品は描かずに項目だけ返す（純粋な関数）。土台が自分の操作と合わせて、rank のいちばん小さいものを主なボタン、残りを「⋯」に入れる
 */
export type ActionProps = {
  /** 請求書の見出しの右。営業力（と現場力の sales の会社）：入金を記録・入金の行 */
  "invoice.head": { invoice: Invoice };
};
export type ActionName = keyof ActionProps;

const nav: NavEntry[] = [];
const routes: RouteEntry[] = [];
const slots: { [K in SlotName]?: { module: Module; C: ComponentType<SlotProps[K]> }[] } = {};
const actionSlots: { [K in ActionName]?: { module: Module; fn: (p: ActionProps[K]) => HeadAction[] }[] } = {};

export function registerNav(e: NavEntry): void {
  if (!nav.some((x) => x.to === e.to)) nav.push(e);
}
export function registerRoute(e: RouteEntry): void {
  if (!routes.some((x) => x.path === e.path)) routes.push(e);
}
export function registerSlot<K extends SlotName>(name: K, module: Module, C: ComponentType<SlotProps[K]>): void {
  const list = (slots[name] ??= []) as { module: Module; C: ComponentType<SlotProps[K]> }[];
  if (!list.some((x) => x.C === C)) list.push({ module, C });
}
/** 見出しの右の操作を足す（#172）。同じ関数は1回だけ */
export function registerActions<K extends ActionName>(name: K, module: Module, fn: (p: ActionProps[K]) => HeadAction[]): void {
  const list = (actionSlots[name] ??= []) as { module: Module; fn: (p: ActionProps[K]) => HeadAction[] }[];
  if (!list.some((x) => x.fn === fn)) list.push({ module, fn });
}

/** その会社でモジュールが有効か（Me が無い＝読み込み中は false） */
export const hasModule = (me: Me | null | undefined, m: Module): boolean => !!me?.tenant.modules.includes(m);
const has = hasModule;
/** hasModule の画面用（useMe から読む） */
export function useModule(m: Module): boolean {
  return hasModule(useMe().data, m);
}

/** 左の帯：key のあとに差し込む項目（その会社で有効なモジュールのものだけ） */
export function navAfter(me: Me, after: NavEntry["after"]): NavEntry[] {
  return nav.filter((x) => x.after === after && has(me, x.module));
}
export function registeredRoutes(): readonly RouteEntry[] {
  return routes;
}
/** その会社でこの画面のモジュールが有効か（無効なら画面は「プランに含まれていません」） */
export function routeAllowed(me: Me | undefined, e: RouteEntry): boolean {
  return has(me, e.module);
}

/** 差し込み口。登録が無い・モジュールが OFF なら何も描かない */
export function Slot<K extends SlotName>({ name, me, ...props }: { name: K; me: Me } & SlotProps[K]) {
  const list = (slots[name] ?? []) as { module: Module; C: ComponentType<SlotProps[K]> }[];
  return (
    <>
      {list
        .filter((x) => has(me, x.module))
        .map(({ C }, i) => (
          <C key={i} {...(props as unknown as SlotProps[K])} />
        ))}
    </>
  );
}

/**
 * 差し込み口の中身を先に読む口（#179、10/13 射場さん）。土台の画面は、自分の答えと差し込みの中身の答えがそろってから描く
 * （差し込みが後から入ると、その下の押す所＝社内メモ・タブなどがずれて押し間違える）。部品が登録する（無ければ待たない）。
 * 渡すのは URL から分かるものだけ（土台の答えを待たずに同時に取りに行く）
 */
export type SlotPreloadProps = {
  "group.received": { groupId: string };
  /** {customer}の「入金予定」（#267 3b）。下の「送付の記録」をずらさない */
  "partner.receivables": { partnerId: string };
};
type PreloadName = keyof SlotPreloadProps;
const preloads: { [K in PreloadName]?: { module: Module; fn: (me: Me, p: SlotPreloadProps[K], qc: QueryClient) => Promise<unknown> }[] } = {};
export function registerSlotPreload<K extends PreloadName>(name: K, module: Module, fn: (me: Me, p: SlotPreloadProps[K], qc: QueryClient) => Promise<unknown>): void {
  const list = (preloads[name] ??= []) as { module: Module; fn: (me: Me, p: SlotPreloadProps[K], qc: QueryClient) => Promise<unknown> }[];
  if (!list.some((x) => x.fn === fn)) list.push({ module, fn });
}
/** 差し込み口の中身を読み終えたか（読めなかった・登録が無い・モジュールが OFF なら待たない＝true）。Me が無い（読み込み中）は false */
export function useSlotPreload<K extends PreloadName>(name: K, me: Me | null | undefined, props: SlotPreloadProps[K]): boolean {
  const qc = useQueryClient();
  const list = ((preloads[name] ?? []) as { module: Module; fn: (me: Me, p: SlotPreloadProps[K], qc: QueryClient) => Promise<unknown> }[]).filter((x) => has(me, x.module));
  const q = useQuery({
    queryKey: ["slot-preload", name, props],
    queryFn: async () => {
      await Promise.all(list.map((x) => x.fn(me!, props, qc).catch(() => undefined)));
      return true;
    },
    enabled: !!me && list.length > 0,
    retry: false,
  });
  if (!me) return false;
  return list.length === 0 || !q.isPending;
}

/** 部品が足した見出しの右の操作（モジュールが OFF・登録が無ければ空）。Me が無い（読み込み中）も空 */
export function slotActions<K extends ActionName>(name: K, me: Me | null | undefined, props: ActionProps[K]): HeadAction[] {
  const list = (actionSlots[name] ?? []) as { module: Module; fn: (p: ActionProps[K]) => HeadAction[] }[];
  return list.filter((x) => has(me, x.module)).flatMap((x) => x.fn(props));
}
